/**
 * ArticleBlockEditor — Notion-style block editor used inside the admin
 * Editorial review modal. Each block is a simple JS object; we keep the
 * UI dead-simple: add-between, move up/down, delete.
 *
 * ITER 66 — Each text-bearing block now has its own Urdu translation
 * pane with a one-click "Translate" button (calls Claude via
 * /api/admin/i18n/articles/{aid}/blocks/translate). Blocks are the SINGLE
 * source of truth — markdown `body` is retired.
 *
 * Block shapes match the renderer in /components/ArticleBlocks.jsx.
 */
import { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Trash, ArrowUp, ArrowDown, Plus, Globe, Check, ArrowCounterClockwise } from "@phosphor-icons/react";

const BLOCK_LIBRARY = [
  { type: "headline",    label: "Headline",     hint: "Section title (H2/H3)" },
  { type: "standfirst",  label: "Standfirst",   hint: "Italic lead paragraph" },
  { type: "text",        label: "Text",         hint: "Paragraph (markdown OK)" },
  { type: "pullquote",   label: "Pull quote",   hint: "Big quoted callout" },
  { type: "callout",     label: "Callout",      hint: "Info / warning / success box" },
  { type: "image",       label: "Image",        hint: "URL + optional caption" },
  { type: "divider",     label: "Divider",      hint: "Horizontal rule" },
  { type: "chart_tracker",        label: "Chart · Tracker",       hint: "Live time-series chart" },
  { type: "chart_snapshot",       label: "Chart · Snapshot",      hint: "One-off result bar chart" },
  { type: "chart_wave_question",  label: "Chart · Wave question", hint: "Bar chart from a specific wave" },
];

// Which fields on each block hold text worth translating. Keep in sync with
// backend `_block_translatable_fields` in routers/i18n.py.
const TRANSLATABLE_FIELDS = {
  headline: ["text"],
  standfirst: ["text"],
  text: ["text"],
  pullquote: ["text"],
  callout: ["text"],
  image: ["caption", "alt"],
  chart_tracker: ["caption"],
  chart_snapshot: ["caption"],
  chart_wave_question: ["caption"],
};

function defaultBlock(type) {
  switch (type) {
    case "headline":   return { type, level: 2, text: "" };
    case "standfirst": return { type, text: "" };
    case "text":       return { type, text: "" };
    case "pullquote":  return { type, text: "" };
    case "callout":    return { type, variant: "info", text: "" };
    case "image":      return { type, url: "", caption: "", alt: "" };
    case "divider":    return { type };
    case "chart_tracker":       return { type, tracker_id: "", caption: "" };
    case "chart_snapshot":      return { type, snapshot_id: "", caption: "" };
    case "chart_wave_question": return { type, wave_id: "", question_id: "", caption: "" };
    default:           return { type };
  }
}

function AddBlockMenu({ onAdd }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full border-2 border-dashed border-neutral-300 hover:border-mango py-2 text-xs text-neutral-500 hover:text-mango flex items-center justify-center gap-2 transition-colors"
        data-testid="block-editor-add"
      >
        <Plus size={14} weight="bold" /> Add block
      </button>
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border-2 border-black hard-shadow max-h-72 overflow-y-auto">
          {BLOCK_LIBRARY.map((b) => (
            <button
              key={b.type}
              type="button"
              onClick={() => { onAdd(b.type); setOpen(false); }}
              className="block w-full text-left px-4 py-2 text-sm hover:bg-neutral-100"
              data-testid={`block-add-${b.type}`}
            >
              <div className="font-bold">{b.label}</div>
              <div className="text-xs text-neutral-500">{b.hint}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function BlockChrome({ index, total, onMove, onDelete, children }) {
  return (
    <div className="border-2 border-neutral-200 bg-white relative group" data-testid={`block-${index}`}>
      <div className="absolute -top-3 right-2 flex gap-1 opacity-100 group-hover:opacity-100">
        <button type="button" onClick={() => onMove(-1)} disabled={index === 0}
          className="bg-white border border-neutral-300 hover:border-black w-7 h-7 inline-flex items-center justify-center disabled:opacity-30"
          data-testid={`block-up-${index}`}><ArrowUp size={12} weight="bold" /></button>
        <button type="button" onClick={() => onMove(1)} disabled={index === total - 1}
          className="bg-white border border-neutral-300 hover:border-black w-7 h-7 inline-flex items-center justify-center disabled:opacity-30"
          data-testid={`block-down-${index}`}><ArrowDown size={12} weight="bold" /></button>
        <button type="button" onClick={onDelete}
          className="bg-white border border-red-300 hover:border-red-600 text-red-600 w-7 h-7 inline-flex items-center justify-center"
          data-testid={`block-delete-${index}`}><Trash size={12} weight="bold" /></button>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ITER 66 — Per-block Urdu editor.
// One panel per translatable field. Shows: status badge, "Translate"
// button (calls backend Claude), RTL Urdu textarea, "Approve" toggle.
// ─────────────────────────────────────────────────────────────
function UrduStatusBadge({ status }) {
  const map = {
    approved: { cls: "bg-emerald-600 text-white",    label: "APPROVED" },
    stale:    { cls: "bg-yellow-500 text-white",     label: "STALE" },
    draft:    { cls: "bg-neutral-500 text-white",    label: "DRAFT" },
    missing:  { cls: "bg-neutral-300 text-neutral-600", label: "MISSING" },
  };
  const m = map[status || "missing"] || map.missing;
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 font-bold ${m.cls}`}>{m.label}</span>;
}

function BlockUrduPane({ index, block, field, articleId, onChange }) {
  const en = (block?.[field] || "").trim();
  const ur = block?.[`${field}_ur`] || "";
  const status = block?.[`${field}_ur_status`] || (ur ? "draft" : "missing");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const setStatus = (next) => onChange({ ...block, [`${field}_ur_status`]: next });
  const setUrText = (val) => onChange({ ...block, [`${field}_ur`]: val, [`${field}_ur_status`]: "draft" });

  const translate = async () => {
    if (!en) { setErr("English text is empty — write it first, then translate."); return; }
    if (!articleId) { setErr("Save the article as a draft first — Urdu translation needs an article ID."); return; }
    setBusy(true); setErr(null);
    try {
      const r = await api.post(`/admin/i18n/articles/${articleId}/blocks/translate`, {
        block_index: index,
        text: en,
        field,
      });
      const newBlock = r.data?.block;
      if (newBlock) onChange({ ...block, ...newBlock });
    } catch (e) {
      setErr(e.response?.data?.detail || "Translation failed");
    } finally {
      setBusy(false);
    }
  };

  if (!en) return null;

  const labelMap = { text: "Text · اردو", caption: "Caption · اردو", alt: "Alt text · اردو" };
  const isMultiline = field === "text";

  return (
    <div className="mt-3 border-t border-dashed border-neutral-300 pt-3 space-y-2" data-testid={`block-${index}-urdu-${field}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <Label className="overline text-neutral-600">{labelMap[field] || `${field} · اردو`}</Label>
        <UrduStatusBadge status={status} />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={translate}
          className="rounded-none h-7 text-xs ml-auto"
          data-testid={`block-${index}-translate-${field}`}
        >
          <Globe size={12} className="mr-1" /> {busy ? "Translating…" : (ur ? "Retranslate" : "Translate to اردو")}
        </Button>
        {ur && status !== "approved" && (
          <Button type="button" variant="ghost" size="sm" onClick={() => setStatus("approved")}
            className="rounded-none h-7 text-xs text-emerald-700 hover:bg-emerald-50"
            data-testid={`block-${index}-approve-${field}`}>
            <Check size={12} className="mr-1" /> Approve
          </Button>
        )}
        {status === "approved" && (
          <Button type="button" variant="ghost" size="sm" onClick={() => setStatus("draft")}
            className="rounded-none h-7 text-xs text-neutral-500"
            data-testid={`block-${index}-unapprove-${field}`}>
            <ArrowCounterClockwise size={12} className="mr-1" /> Back to draft
          </Button>
        )}
      </div>
      {isMultiline ? (
        <textarea
          dir="rtl"
          lang="ur"
          rows={4}
          value={ur}
          onChange={(e) => setUrText(e.target.value)}
          placeholder="اردو ترجمہ — ابھی لکھیں یا ترجمہ کا بٹن دبائیں"
          className="w-full border-2 border-neutral-200 rounded-none px-3 py-2 text-base text-right"
          data-testid={`block-${index}-urdu-input-${field}`}
        />
      ) : (
        <Input
          dir="rtl"
          lang="ur"
          value={ur}
          onChange={(e) => setUrText(e.target.value)}
          placeholder="اردو ترجمہ"
          className="rounded-none text-right"
          data-testid={`block-${index}-urdu-input-${field}`}
        />
      )}
      {err && <div className="text-xs text-red-600" data-testid={`block-${index}-urdu-err`}>{err}</div>}
    </div>
  );
}

function HeadlineEditor({ block, onChange }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <Label className="overline">Headline</Label>
        <select value={block.level || 2} onChange={(e) => onChange({ ...block, level: parseInt(e.target.value, 10) })} className="border border-neutral-300 text-xs px-1 py-0.5">
          <option value={1}>H1</option>
          <option value={2}>H2</option>
          <option value={3}>H3</option>
        </select>
      </div>
      <Input value={block.text || ""} onChange={(e) => onChange({ ...block, text: e.target.value })} className="rounded-none text-lg font-bold" placeholder="Section title" />
    </div>
  );
}

function StandfirstEditor({ block, onChange }) {
  return (
    <div className="space-y-2">
      <Label className="overline">Standfirst</Label>
      <textarea rows={2} value={block.text || ""} onChange={(e) => onChange({ ...block, text: e.target.value })} className="w-full border-2 border-neutral-200 rounded-none px-3 py-2 italic text-base" placeholder="One- or two-sentence lead under the headline." />
    </div>
  );
}

function TextEditor({ block, onChange }) {
  return (
    <div className="space-y-2">
      <Label className="overline">Text — markdown OK (**bold**, *italic*, [link](url))</Label>
      <textarea rows={5} value={block.text || ""} onChange={(e) => onChange({ ...block, text: e.target.value })} className="w-full border-2 border-neutral-200 rounded-none px-3 py-2 text-base" placeholder="Write a paragraph or two." />
    </div>
  );
}

function PullQuoteEditor({ block, onChange }) {
  return (
    <div className="space-y-2">
      <Label className="overline">Pull quote</Label>
      <textarea rows={2} value={block.text || ""} onChange={(e) => onChange({ ...block, text: e.target.value })} className="w-full border-2 border-neutral-200 rounded-none px-3 py-2 text-base font-bold" placeholder="A short, punchy quote pulled from the article." />
    </div>
  );
}

function CalloutEditor({ block, onChange }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <Label className="overline">Callout</Label>
        <select value={block.variant || "info"} onChange={(e) => onChange({ ...block, variant: e.target.value })} className="border border-neutral-300 text-xs px-1 py-0.5">
          <option value="info">Info (blue)</option>
          <option value="warning">Warning (amber)</option>
          <option value="success">Success (green)</option>
        </select>
      </div>
      <textarea rows={3} value={block.text || ""} onChange={(e) => onChange({ ...block, text: e.target.value })} className="w-full border-2 border-neutral-200 rounded-none px-3 py-2 text-sm" placeholder="Markdown supported." />
    </div>
  );
}

function ImageEditor({ block, onChange }) {
  return (
    <div className="space-y-2">
      <Label className="overline">Image</Label>
      <Input value={block.url || ""} onChange={(e) => onChange({ ...block, url: e.target.value })} className="rounded-none" placeholder="Image URL (paste from CDN, Imgur, Cloudinary)" />
      <Input value={block.caption || ""} onChange={(e) => onChange({ ...block, caption: e.target.value })} className="rounded-none text-xs" placeholder="Caption (optional)" />
      <Input value={block.alt || ""} onChange={(e) => onChange({ ...block, alt: e.target.value })} className="rounded-none text-xs" placeholder="Alt text (accessibility)" />
      {block.url && <img src={block.url} alt="" className="mt-2 max-h-40 border border-neutral-200" />}
    </div>
  );
}

function DividerEditor() {
  return <div className="border-t-2 border-neutral-300 my-2" />;
}

function ChartTrackerEditor({ block, onChange, options }) {
  return (
    <div className="space-y-2">
      <Label className="overline">Chart · Tracker (live)</Label>
      <select value={block.tracker_id || ""} onChange={(e) => onChange({ ...block, tracker_id: e.target.value })} className="w-full border-2 border-neutral-200 rounded-none px-3 py-2 text-sm" data-testid={`chart-tracker-pick`}>
        <option value="">-- pick a tracker --</option>
        {(options?.trackers || []).map((t) => (
          <option key={t.id} value={t.id}>{t.name} {t.sector ? `· ${t.sector}` : ""}</option>
        ))}
      </select>
      <Input value={block.caption || ""} onChange={(e) => onChange({ ...block, caption: e.target.value })} className="rounded-none text-xs" placeholder="Caption (optional)" />
    </div>
  );
}

function ChartSnapshotEditor({ block, onChange, options }) {
  return (
    <div className="space-y-2">
      <Label className="overline">Chart · Snapshot (live)</Label>
      <select value={block.snapshot_id || ""} onChange={(e) => onChange({ ...block, snapshot_id: e.target.value })} className="w-full border-2 border-neutral-200 rounded-none px-3 py-2 text-sm">
        <option value="">-- pick a snapshot --</option>
        {(options?.snapshots || []).map((s) => (
          <option key={s.id} value={s.id}>{s.question_text || s.id}</option>
        ))}
      </select>
      <Input value={block.caption || ""} onChange={(e) => onChange({ ...block, caption: e.target.value })} className="rounded-none text-xs" placeholder="Caption (optional)" />
    </div>
  );
}

function ChartWaveQuestionEditor({ block, onChange, options }) {
  const wave = (options?.waves || []).find((w) => w.id === block.wave_id);
  return (
    <div className="space-y-2">
      <Label className="overline">Chart · Wave question (live)</Label>
      <select value={block.wave_id || ""} onChange={(e) => onChange({ ...block, wave_id: e.target.value, question_id: "" })} className="w-full border-2 border-neutral-200 rounded-none px-3 py-2 text-sm">
        <option value="">-- pick a closed wave --</option>
        {(options?.waves || []).map((w) => (
          <option key={w.id} value={w.id}>{w.name}</option>
        ))}
      </select>
      {wave && (
        <select value={block.question_id || ""} onChange={(e) => onChange({ ...block, question_id: e.target.value })} className="w-full border-2 border-neutral-200 rounded-none px-3 py-2 text-sm">
          <option value="">-- pick a question --</option>
          {wave.questions.map((q) => (
            <option key={q.id} value={q.id}>{q.text}</option>
          ))}
        </select>
      )}
      <Input value={block.caption || ""} onChange={(e) => onChange({ ...block, caption: e.target.value })} className="rounded-none text-xs" placeholder="Caption (optional)" />
    </div>
  );
}

const EDITOR_COMPONENTS = {
  headline: HeadlineEditor,
  standfirst: StandfirstEditor,
  text: TextEditor,
  pullquote: PullQuoteEditor,
  callout: CalloutEditor,
  image: ImageEditor,
  divider: DividerEditor,
  chart_tracker: ChartTrackerEditor,
  chart_snapshot: ChartSnapshotEditor,
  chart_wave_question: ChartWaveQuestionEditor,
};

export default function ArticleBlockEditor({ blocks, onChange, legacyBody, articleId, onTranslateAll }) {
  const [options, setOptions] = useState({ trackers: [], snapshots: [], waves: [] });
  const [translatingAll, setTranslatingAll] = useState(false);
  useEffect(() => {
    api.get("/admin/articles/blocks-options").then((r) => setOptions(r.data)).catch(() => {});
  }, []);

  const list = Array.isArray(blocks) ? blocks : [];

  const updateAt = (i, next) => onChange(list.map((b, idx) => (idx === i ? next : b)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const copy = [...list];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    onChange(copy);
  };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const addAt = (type, position) => {
    const block = defaultBlock(type);
    const copy = [...list];
    copy.splice(position, 0, block);
    onChange(copy);
  };

  const convertFromLegacy = () => {
    if (!legacyBody) return;
    // Split markdown by double newlines, treat lines starting with # as headlines and > as pullquotes.
    const out = [];
    legacyBody.split(/\n{2,}/).forEach((para) => {
      const t = para.trim();
      if (!t) return;
      const hMatch = t.match(/^(#{1,3})\s+(.*)$/m);
      if (hMatch) { out.push({ type: "headline", level: hMatch[1].length, text: hMatch[2].trim() }); return; }
      if (t.startsWith(">")) { out.push({ type: "pullquote", text: t.replace(/^>\s?/gm, "").trim() }); return; }
      out.push({ type: "text", text: t });
    });
    onChange(out);
  };

  // ITER 66 — one-click translate every missing block (retroactive support)
  const translateAllMissing = async () => {
    if (!articleId) return;
    setTranslatingAll(true);
    try {
      const r = await api.post(`/admin/i18n/articles/${articleId}/draft`);
      // Backend returns the fresh article — hydrate blocks in-place.
      const fresh = r.data;
      if (fresh?.blocks) onChange(fresh.blocks);
      if (onTranslateAll) onTranslateAll(fresh);
    } finally {
      setTranslatingAll(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="article-block-editor">
      {list.length === 0 && legacyBody && (
        <div className="bg-amber-50 border-2 border-amber-600 p-4 text-sm flex items-center justify-between">
          <div>
            <div className="overline text-amber-900 mb-1">LEGACY MARKDOWN DETECTED</div>
            <div className="text-amber-900">This article was written in the old markdown editor. Convert it to blocks to edit + translate visually.</div>
          </div>
          <Button onClick={convertFromLegacy} className="rounded-none bg-amber-600 hover:bg-amber-700 text-white" data-testid="convert-legacy">Convert</Button>
        </div>
      )}
      {list.length > 0 && articleId && (
        <div className="bg-neutral-50 border border-neutral-200 p-3 flex items-center justify-between flex-wrap gap-2" data-testid="block-editor-urdu-toolbar">
          <div className="text-xs text-neutral-600">
            <strong>اردو:</strong> Save first — every block below has its own translate button. Or use one-click:
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-none"
            disabled={translatingAll}
            onClick={translateAllMissing}
            data-testid="translate-all-blocks"
          >
            <Globe size={14} className="mr-1" /> {translatingAll ? "Translating all blocks…" : "Translate all missing blocks to اردو"}
          </Button>
        </div>
      )}
      <AddBlockMenu onAdd={(type) => addAt(type, 0)} />
      {list.map((block, i) => {
        const Editor = EDITOR_COMPONENTS[block.type];
        if (!Editor) return null;
        const trFields = TRANSLATABLE_FIELDS[block.type] || [];
        return (
          <div key={i}>
            <BlockChrome index={i} total={list.length} onMove={(d) => move(i, d)} onDelete={() => remove(i)}>
              <Editor block={block} onChange={(next) => updateAt(i, next)} options={options} />
              {trFields.length > 0 && trFields.map((f) => (
                <BlockUrduPane
                  key={f}
                  index={i}
                  block={block}
                  field={f}
                  articleId={articleId}
                  onChange={(next) => updateAt(i, next)}
                />
              ))}
            </BlockChrome>
            <div className="my-1">
              <AddBlockMenu onAdd={(type) => addAt(type, i + 1)} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
